import { en } from '../constant/en'
import { id } from '../constant/id'
import { useUiFromSpecContext } from '../EditorContextProvider'

/**
 * Deep merge objects
 */
const deepMerge = (target: any, source: any): any => {
  if (!source) return target

  const result = { ...target }

  for (const key in source) {
    if (source[key] && typeof source[key] === 'object' && !Array.isArray(source[key])) {
      result[key] = deepMerge(target[key] || {}, source[key])
    } else {
      result[key] = source[key]
    }
  }

  return result
}

/**
 * Custom hook for accessing translations based on current language
 *
 * @returns Object containing current translations and utility functions
 */
export const useTranslation = () => {
  const { language, customTranslations } = useUiFromSpecContext()
  const translations = getTranslations(language, customTranslations)

  /**
   * Get a translation value by nested key path
   *
   * @param keyPath - Dot-separated path to the translation key (e.g., 'common.logout')
   * @param params - Optional parameters for string interpolation
   * @returns Translated string
   */
  const t = (keyPath: string, params?: Record<string, string | number>, fallback?: string) => {
    const keys = keyPath.split('.')
    let value: unknown = translations

    for (const key of keys) {
      value = (value as Record<string, unknown>)?.[key]
      if (value === undefined) {
        // console.warn(`Translation key not found: ${keyPath}`)
        return fallback || keyPath // Return the key path if translation not found
      }
    }

    if (typeof value !== 'string') {
      console.warn(`Translation value is not a string: ${keyPath}`)
      return fallback || keyPath
    }

    // Handle string interpolation if params are provided
    if (params) {
      return value.replace(/\{\{(\w+)\}\}/g, (match: string, paramKey: string) => {
        return params[paramKey]?.toString() || match
      })
    }

    return value
  }

  return {
    t,
    language,
    translations,
  }
}

export const defaultTranslations = {
  en,
  id,
}

export const getTranslations = (language?: 'en' | 'id', customTranslations?: any) => {
  const baseTranslations = defaultTranslations[language ?? 'en'] || defaultTranslations.en

  if (customTranslations && customTranslations[language ?? 'en']) {
    return deepMerge(baseTranslations, customTranslations[language ?? 'en'])
  }

  return baseTranslations
}
